import React from 'react';
import { useTranslation } from 'react-i18next';
// Its own one-key namespace, never `../../i18n`: this is in the initial bundle.
import { NS } from './i18n';
import './KnowledgeSkeleton.css';

/** Files, Text, Websites, FAQ, Guidelines, Acronyms — the page's six sources. */
const SOURCE_ROWS = [0, 1, 2, 3, 4, 5];
/** Item cards drawn in the list — enough to fill a laptop-height column. */
const ITEM_ROWS = [0, 1, 2, 3];

const Bone: React.FC<{ className: string }> = ({ className }) => (
  <span className={`knowledge-skeleton__bone ${className}`} />
);

/** One source row (`KnowledgeSources`): icon, title and description, the action button. */
const SourceSkeleton: React.FC = () => (
  <div className="knowledge-skeleton__source">
    <div className="knowledge-skeleton__source-content">
      <Bone className="knowledge-skeleton__icon" />
      <div className="knowledge-skeleton__source-text">
        <Bone className="knowledge-skeleton__line knowledge-skeleton__line--source-title" />
        <Bone className="knowledge-skeleton__line knowledge-skeleton__line--source-description" />
      </div>
    </div>
    <Bone className="knowledge-skeleton__button" />
  </div>
);

/** One item card (`KnowledgeList`): icon, name, type; the ⋯ menu; status and date. */
const ItemSkeleton: React.FC = () => (
  <div className="knowledge-skeleton__item">
    <div className="knowledge-skeleton__item-head">
      <Bone className="knowledge-skeleton__item-icon" />
      <div className="knowledge-skeleton__item-text">
        <Bone className="knowledge-skeleton__line knowledge-skeleton__line--item-name" />
        <Bone className="knowledge-skeleton__line knowledge-skeleton__line--item-type" />
      </div>
      <Bone className="knowledge-skeleton__more" />
    </div>
    <div className="knowledge-skeleton__item-foot">
      <Bone className="knowledge-skeleton__tag" />
      <Bone className="knowledge-skeleton__line knowledge-skeleton__line--date" />
    </div>
  </div>
);

/** The list card (`.knowledge-list-card`) with its item cards. */
const ListSkeleton: React.FC = () => (
  <div className="knowledge-skeleton__list">
    {ITEM_ROWS.map((row) => (
      <ItemSkeleton key={row} />
    ))}
  </div>
);

export interface KnowledgeSkeletonProps {
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome.
   */
  standalone?: boolean;
  /**
   * `list` draws only the list card, for the page itself while its sources load
   * (the rest of the page is already there).
   */
  part?: 'page' | 'list';
  /**
   * What the status says. The page passes its own translated copy; elsewhere the
   * skeleton's namespace has it.
   */
  label?: string;
}

/**
 * Loading shape of the knowledge base (SL-1909, part 2). One shape from a cold
 * load to data: `RouteSkeleton` (inside the app chrome), the agent layouts while
 * the agent loads and the route's Suspense fallback draw this; the page then
 * mounts with the same panel, its list card showing the same item cards
 * (`part="list"`) while the sources load.
 *
 * The page as it arrives: breadcrumb, then one panel — the title and the six
 * sources on the left half, the search and the list card on the right. On a
 * phone the two halves stack, as on the page.
 *
 * Its own classes, never the page's: the page's sheets come with its chunk and
 * this paints before it. Nothing visible but bones; the status carries its
 * translated text inside, visually hidden, so a screen reader announces it (a
 * label alone on an empty live region is often never read). No `aria-busy`:
 * on a live region it tells assistive tech to hold the announcement.
 */
export const KnowledgeSkeleton: React.FC<KnowledgeSkeletonProps> = ({
  standalone = false,
  part = 'page',
  label,
}) => {
  const { t } = useTranslation(NS);
  const text = label ?? t('loading');

  if (part === 'list') {
    return (
      <div
        className="knowledge-skeleton knowledge-skeleton--list"
        role="status"
        data-testid="knowledge-list-skeleton"
      >
        <span className="knowledge-skeleton__sr-only">{text}</span>
        <div className="knowledge-skeleton__list-slot" aria-hidden="true">
          <ListSkeleton />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`knowledge-skeleton${standalone ? ' knowledge-skeleton--standalone' : ''}`}
      role="status"
      data-testid="knowledge-skeleton"
    >
      <span className="knowledge-skeleton__sr-only">{text}</span>
      <div className="knowledge-skeleton__header" aria-hidden="true">
        <Bone className="knowledge-skeleton__crumb" />
      </div>
      <div className="knowledge-skeleton__panel" aria-hidden="true">
        <div className="knowledge-skeleton__sources">
          <Bone className="knowledge-skeleton__title" />
          <div className="knowledge-skeleton__source-list">
            {SOURCE_ROWS.map((row) => (
              <SourceSkeleton key={row} />
            ))}
          </div>
        </div>
        <div className="knowledge-skeleton__list-pane">
          <Bone className="knowledge-skeleton__search" />
          <ListSkeleton />
        </div>
      </div>
    </div>
  );
};
